import {
    CreateButton,
    DataTable,
    DateField,
    ExportButton,
    List,
    ReferenceField,
    SearchInput,
    TextField,
} from '@/components/admin';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Calendar, DollarSign } from 'lucide-react';
import { useGetList, FilterLiveForm, useRecordContext } from 'ra-core';
import { StatusBadge } from '../documents/shared/StatusBadge';
import { Filter as FilterIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';

const StatusCell = () => {
    const record = useRecordContext();
    if (!record) return null;
    return <StatusBadge status={record.status} />;
};

const TypeBadge = () => {
    const record = useRecordContext();
    if (!record?.project_type) return <span className="text-xs text-slate-400">—</span>;
    const isRepeat = record.project_type === 'repeat_order';
    return (
        <Badge variant="outline" className={`text-[10px] font-semibold ${isRepeat ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-sky-50 text-sky-700 border-sky-200'}`}>
            {isRepeat ? 'Repeat' : 'New'}
        </Badge>
    );
};

const ValueCell = () => {
    const record = useRecordContext();
    if (!record?.estimated_value) return <span className="text-slate-400 text-xs">—</span>;
    return (
        <div className="flex items-center gap-1 font-bold text-green-700">
            <DollarSign className="w-3 h-3" />
            {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(record.estimated_value)}
        </div>
    );
};

const StatCard = ({ title, value, colorClass, increase, trendClass }: any) => (
    <Card className="flex-1 p-5 shadow-sm border-none rounded-[1.25rem] bg-white flex flex-col gap-3">
        <div className="text-sm font-semibold text-slate-500">{title}</div>
        <div className="flex items-end justify-between">
            <div className="text-4xl font-bold text-slate-800">{value}</div>
            {/* Simple mock dots diagram from image */}
            <div className={`flex items-end gap-1 h-8 ${colorClass}`}>
                <div className="w-1.5 h-3 bg-current rounded-full opacity-40"></div>
                <div className="w-1.5 h-5 bg-current rounded-full opacity-60"></div>
                <div className="w-1.5 h-4 bg-current rounded-full opacity-80"></div>
                <div className="w-1.5 h-7 bg-current rounded-full"></div>
                <div className="w-1.5 h-5 bg-current rounded-full opacity-90"></div>
            </div>
        </div>
        <div className="text-xs text-slate-400 mt-1 flex items-center gap-2">
            <span className={`font-medium px-1.5 py-0.5 rounded ${trendClass}`}>{increase}</span>
            <span>last week</span>
        </div>
    </Card>
);

const ProjectStats = () => {
    const { total: active, isPending: p1 } = useGetList('projects', { filter: { status: 'active' }, pagination: { page: 1, perPage: 1 } });
    const { total: onHold, isPending: p2 } = useGetList('projects', { filter: { status: 'on_hold' }, pagination: { page: 1, perPage: 1 } });
    const { total: completed, isPending: p3 } = useGetList('projects', { filter: { status: 'completed' }, pagination: { page: 1, perPage: 1 } });
    const { total: cancelled, isPending: p4 } = useGetList('projects', { filter: { status: 'cancelled' }, pagination: { page: 1, perPage: 1 } });

    return (
        <div className="flex w-full flex-col sm:flex-row gap-4 mb-6">
            <StatCard title="Active Projects" value={p1 ? '-' : active} colorClass="text-[#00a68d]" increase="↑ 23%" trendClass="bg-green-100 text-[#00a68d]" />
            <StatCard title="On Hold Projects" value={p2 ? '-' : onHold} colorClass="text-blue-500" increase="↓ 12%" trendClass="bg-red-100 text-red-600" />
            <StatCard title="Completed Projects" value={p3 ? '-' : completed} colorClass="text-amber-500" increase="↑ 20%" trendClass="bg-green-100 text-[#00a68d]" />
            <StatCard title="Cancelled Projects" value={p4 ? '-' : cancelled} colorClass="text-red-500" increase="↓ 04%" trendClass="bg-red-100 text-red-600" />
        </div>
    );
};

const ProjectListActions = () => (
    <div className="flex w-full items-center justify-between mb-4 mt-2">
        <FilterLiveForm>
            <SearchInput source="q" placeholder="Search..." alwaysOn className="w-64 bg-white rounded-lg border-slate-200" />
        </FilterLiveForm>
        <div className="flex items-center gap-2">
            <Button variant="outline" className="flex items-center gap-2 text-slate-600 font-medium">
                <FilterIcon className="w-4 h-4" /> Filter by
            </Button>
            <ExportButton />
            <CreateButton label="New Project" className="bg-[#00a68d] hover:bg-[#008f79]" />
        </div>
    </div>
);
export const ProjectList = () => (
    <List
        title=""
        actions={false}
        filterDefaultValues={{ status: 'active' }}
        sort={{ field: 'start_date', order: 'DESC' }}
        className="pt-2 pb-6 min-h-full"
    >
        <div className="w-full flex-1">
            <ProjectStats />
            <ProjectListActions />
            <Card className="border-none shadow-sm rounded-xl overflow-hidden bg-white">
                <DataTable rowClick="show" className="border-none">
                    <DataTable.Col
                        source="reference"
                        label="Project No."
                        className="font-mono font-bold text-slate-900"
                    />
                    <DataTable.Col
                        source="name"
                        label="Project Name"
                        className="font-medium max-w-[200px] truncate"
                    />
                    <DataTable.Col label="Client">
                        <ReferenceField source="company_id" reference="companies" link={false}>
                            <TextField source="name" className="text-blue-600 font-medium" />
                        </ReferenceField>
                    </DataTable.Col>
                    <DataTable.Col label="Type">
                        <TypeBadge />
                    </DataTable.Col>
                    <DataTable.Col label="Status">
                        <StatusCell />
                    </DataTable.Col>
                    <DataTable.Col label="Value">
                        <ValueCell />
                    </DataTable.Col>
                    <DataTable.Col label="Start Date">
                        <div className="flex items-center gap-2 text-slate-500 text-xs">
                            <Calendar className="w-3 h-3" />
                            <DateField source="start_date" />
                        </div>
                    </DataTable.Col>
                </DataTable>
            </Card>
        </div>
    </List>
);
